import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ActivityDays,
  ActivityOverview,
  getActivity,
  screenLabel
} from '../../api/services/activityService';
import { HeaderActions, KpiCard, LinkButton } from '../../components/ui/dg';

/**
 * Activité de l'app — mesure maison (API migration 082) : qui utilise l'app,
 * combien de temps, sur quel téléphone, avec quelle version, et quels écrans.
 *
 * Les sessions et les écrans n'existent qu'à partir de la version de l'app qui
 * les envoie ; la répartition Android / iOS de « tous les comptes », elle, se
 * lit dans le dernier appel de chaque compte à l'API et couvre aussi les
 * anciennes versions.
 *
 * Couleurs de données : Android = sarcelle, iOS = indigo (paire validée pour
 * le daltonisme, comme l'analytics des locations) ; gris = inconnu.
 */

const PERIODS: { days: ActivityDays; label: string; long: string }[] = [
  { days: 1, label: '24 h', long: '24 heures' },
  { days: 7, label: '7 j', long: '7 jours' },
  { days: 30, label: '30 j', long: '30 jours' },
  { days: 90, label: '90 j', long: '90 jours' }
];

const PLATFORM_COLOR: Record<string, string> = { android: '#4F46E5', ios: '#A5A1F2', unknown: '#E5E7EB' };

const nf = new Intl.NumberFormat('fr-FR');
const fmt = (n: number) => nf.format(Math.round(n || 0));
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const duration = (seconds: number) => {
  if (!seconds) return '0 s';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return minutes < 60 ? `${minutes} min${rest ? ` ${rest} s` : ''}` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

const card = 'rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const cardTitle = 'text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]';
const Skeleton = ({ className }: { className: string }) => <div className={`${className} animate-pulse rounded-xl bg-[#EEF0F5]`} />;

/** Variation vs période précédente. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Barres empilées Android / iOS (utilisateurs actifs par jour). */
const DailyBars = ({ data }: { data: ActivityOverview['daily'] }) => {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const height = 200;
  const pad = { top: 10, right: 8, bottom: 24, left: 32 };
  const innerW = Math.max(width - pad.left - pad.right, 10);
  const innerH = height - pad.top - pad.bottom;
  const peak = Math.max(1, ...data.map((d) => d.users));
  const max = peak <= 4 ? 4 : Math.ceil(peak / 4) * 4;
  const slot = innerW / Math.max(data.length, 1);
  const barW = Math.max(Math.min(slot * 0.7, 28), 2);
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(innerW / 64))));
  const label = (iso: string, long = false) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', long ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'short' });
  const hovered = hover !== null ? data[hover] : null;

  return (
    <div ref={wrapRef} className="relative w-full select-none">
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Utilisateurs actifs par jour, Android et iOS">
          {[0, max / 2, max].map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke={t === 0 ? '#E5E7EB' : '#F1F2F5'} />
              <text x={pad.left - 8} y={y(t)} dy="0.32em" textAnchor="end" fill="#9CA3AF" fontSize={10.5}>{fmt(t)}</text>
            </g>
          ))}
          {data.map((d, i) => {
            const x = pad.left + i * slot + (slot - barW) / 2;
            const other = Math.max(d.users - d.android_users - d.ios_users, 0);
            const parts = [
              { key: 'android', v: d.android_users },
              { key: 'ios', v: d.ios_users },
              { key: 'unknown', v: other }
            ];
            let base = 0;
            return (
              <g key={d.day} opacity={hover === null || hover === i ? 1 : 0.45}>
                {parts.map((p) => {
                  if (p.v <= 0) return null;
                  const top = y(base + p.v);
                  const h = y(base) - top;
                  base += p.v;
                  return <rect key={p.key} x={x} y={top} width={barW} height={Math.max(h, 1)} rx={4} fill={PLATFORM_COLOR[p.key]} />;
                })}
                {i % every === 0 && (
                  <text x={x + barW / 2} y={height - 8} textAnchor="middle" fill="#9CA3AF" fontSize={10.5}>{label(d.day)}</text>
                )}
                <rect
                  x={pad.left + i * slot} y={pad.top} width={slot} height={innerH} fill="transparent"
                  onPointerEnter={() => setHover(i)} onPointerDown={() => setHover(i)} onPointerLeave={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {hovered && (
        <div className="pointer-events-none absolute right-2 top-0 z-10 w-48 rounded-xl border border-gray-100 bg-white px-3 py-2 text-[12px] shadow-lg">
          <p className="font-medium text-gray-900 first-letter:uppercase">{label(hovered.day, true)}</p>
          <p className="mt-1 flex justify-between text-gray-600"><span>Utilisateurs</span><span className="font-medium tabular-nums text-gray-900">{fmt(hovered.users)}</span></p>
          <p className="flex justify-between text-gray-600"><span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: PLATFORM_COLOR.android }} />Android</span><span className="tabular-nums">{fmt(hovered.android_users)}</span></p>
          <p className="flex justify-between text-gray-600"><span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: PLATFORM_COLOR.ios }} />iOS</span><span className="tabular-nums">{fmt(hovered.ios_users)}</span></p>
          <p className="flex justify-between text-gray-600"><span>Sessions</span><span className="tabular-nums">{fmt(hovered.sessions)}</span></p>
        </div>
      )}
    </div>
  );
};

const ManagementActivity = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fromUrl = Number(params.get('jours'));
  const [days, setDays] = useState<ActivityDays>(PERIODS.some((p) => p.days === fromUrl) ? (fromUrl as ActivityDays) : 30);
  const [data, setData] = useState<ActivityOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const period = PERIODS.find((p) => p.days === days) || PERIODS[2];

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getActivity(token, days));
    } catch (e: any) {
      setError(e?.message || 'Activité indisponible');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  const selectPeriod = (value: ActivityDays) => {
    setDays(value);
    const updated = new URLSearchParams(params);
    if (value === 30) updated.delete('jours'); else updated.set('jours', String(value));
    setParams(updated, { replace: true });
  };

  const periods = (
    <HeaderActions>
      <div className="flex items-center gap-2" role="tablist" aria-label="Période">
        {PERIODS.map((p) => (
          <button key={p.days} type="button" role="tab" aria-selected={p.days === days} onClick={() => selectPeriod(p.days)}
            className={`h-10 rounded-full border px-4 text-[13.5px] font-bold transition-colors ${p.days === days ? 'border-[#1E293B] bg-[#1E293B] text-white' : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-gray-50'}`}>
            {p.label}
          </button>
        ))}
      </div>
    </HeaderActions>
  );

  if (error && !data) {
    return (
      <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
        {periods}
        <div className={`${card} px-4 py-12 text-center`}>
          <p className="text-[13.5px] text-[#B91C1C]">{error}</p>
          <button type="button" onClick={load} className="mt-3 h-10 rounded-[20px] bg-[#4F46E5] px-4 text-[13.5px] font-bold text-white">Réessayer</button>
        </div>
      </main>
    );
  }

  const t = data?.totals;
  const tp = data?.totals_prev;
  const trend = t && tp && tp.active_users > 0 ? Math.round(((t.active_users - tp.active_users) / tp.active_users) * 100) : null;
  const platformUsers = (key: string) => (data?.platforms || []).filter((p) => p.platform === key).reduce((sum, p) => sum + p.users, 0);
  const android = platformUsers('android');
  const ios = platformUsers('ios');
  const known = android + ios;
  const androidShare = pct(android, known);
  const deviceTotal = (data?.devices || []).reduce((sum, d) => sum + d.users, 0);
  const deviceAndroid = pct((data?.devices || []).filter((d) => d.platform === 'android').reduce((sum, d) => sum + d.users, 0), deviceTotal);
  // Une version peut exister sur les deux plateformes : on les additionne.
  const versions = Object.entries((data?.versions || []).reduce<Record<string, number>>((acc, v) => {
    acc[v.app_version || '?'] = (acc[v.app_version || '?'] || 0) + v.users;
    return acc;
  }, {})).sort((x, y) => y[1] - x[1]);
  const versionTotal = versions.reduce((sum, [, n]) => sum + n, 0);
  const VERSION_COLORS = ['#4F46E5', '#A5A1F2', '#C7C4F6', '#F59E0B', '#F59E0B'];
  const SC = 'grid-cols-[minmax(0,1.6fr)_0.7fr_0.7fr_0.8fr]';

  return (
    <main className="flex-1 space-y-5 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
      {periods}
      {data && t && t.sessions === 0 && (
        <p className="rounded-2xl bg-[#FFF8EB] px-4 py-3 text-[13px] leading-[1.5] text-[#92400E]">
          Aucune session sur la période. Les sessions et les écrans arrivent avec la nouvelle version de l’app.
        </p>
      )}

      <div className={`grid grid-cols-2 gap-5 lg:grid-cols-4 ${loading && data ? 'opacity-60' : ''}`}>
        <KpiCard label="Utilisateurs actifs" value={t ? fmt(t.active_users) : '…'} valueColor="#4F46E5" subColor="#6B7280"
          sub={trend === null ? `sur ${period.long}` : `${trend > 0 ? '+' : ''}${trend} % vs période précédente`} />
        <KpiCard label="Sessions" value={t ? fmt(t.sessions) : '…'} sub={t && t.active_users ? `${nf.format(Math.round((t.sessions / t.active_users) * 10) / 10)} par utilisateur` : 'sur la période'} />
        <KpiCard label="Durée médiane" value={t ? duration(t.median_session_seconds) : '…'} sub="par session" />
        <KpiCard label="Écrans par session" value={t ? nf.format(t.screens_per_session) : '…'} sub="en moyenne" />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <section className={`${card} px-7 pb-5 pt-5`}>
          <div className="flex items-center justify-between gap-3">
            <h2 className={cardTitle}>Utilisateurs actifs par jour</h2>
            <span className="flex items-center gap-3.5 text-[12.5px] text-[#6B7280]">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: PLATFORM_COLOR.android }} />Android</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: PLATFORM_COLOR.ios }} />iOS</span>
            </span>
          </div>
          <div className="mt-4">
            {loading && !data ? <Skeleton className="h-[200px] w-full" /> : data && data.daily.length > 0 ? <DailyBars data={data.daily} /> : (
              <p className="py-10 text-center text-[13px] text-[#6B7280]">Pas encore de session sur la période.</p>
            )}
          </div>
        </section>

        <section className={`${card} px-7 pb-5 pt-5`}>
          <h2 className={cardTitle}>Android ou iOS</h2>
          {!data ? <Skeleton className="mt-4 h-40 w-full" /> : (
            <div className="mt-4 flex items-center gap-6">
              <svg width="150" height="150" viewBox="0 0 42 42" className="shrink-0" role="img" aria-label={`${androidShare} % Android`}>
                <circle cx="21" cy="21" r="15.9" fill="none" stroke={known ? PLATFORM_COLOR.ios : '#EEF0F5'} strokeWidth="6" />
                {known > 0 && <circle cx="21" cy="21" r="15.9" fill="none" stroke={PLATFORM_COLOR.android} strokeWidth="6" strokeDasharray={`${androidShare} ${100 - androidShare}`} strokeDashoffset="25" />}
                <text x="21" y="20" textAnchor="middle" fontSize="7" fontWeight="800" fill="#1E293B">{known ? `${androidShare} %` : '—'}</text>
                <text x="21" y="26" textAnchor="middle" fontSize="3.4" fill="#6B7280">Android</text>
              </svg>
              <div className="min-w-0 flex-1">
                {[['Android', android, androidShare, 'android'], ['iPhone', ios, known ? 100 - androidShare : 0, 'ios']].map(([label, users, share, key], i) => (
                  <div key={key as string} className={`flex items-center gap-3 py-2.5 ${i ? 'border-t border-[#EEF0F4]' : ''}`}>
                    <span className="h-3 w-3 shrink-0 rounded-[4px]" style={{ background: PLATFORM_COLOR[key as string] }} />
                    <span className="min-w-0 flex-1">
                      <b className="block text-[14px] font-bold">{label}</b>
                      <span className="block text-[12.5px] text-[#6B7280]">{fmt(users as number)} utilisateurs</span>
                    </span>
                    <b className="text-[14px]">{share} %</b>
                  </div>
                ))}
                {deviceTotal > 0 && <p className="mt-2 text-[12px] text-[#6B7280]">Sur tous les comptes, {deviceAndroid} % sur Android.</p>}
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <section className={`${card} overflow-hidden`}>
          <div className={`grid ${SC} gap-3 px-6 pb-2.5 pt-5`}>
            <h2 className={cardTitle}>Écrans les plus vus</h2>
            {['Vues', 'Comptes', 'Temps moyen'].map((h) => <span key={h} className={`${cardTitle} text-right`}>{h}</span>)}
          </div>
          {data && data.screens.length > 0 ? data.screens.slice(0, 8).map((sc) => (
            <div key={sc.screen} className={`grid ${SC} items-center gap-3 border-t border-[#EEF0F4] px-6 py-2.5`}>
              <span className="truncate text-[14px] font-semibold text-[#1E293B]">{screenLabel(sc.screen)}</span>
              <span className="text-right text-[13.5px] tabular-nums">{fmt(sc.views)}</span>
              <span className="text-right text-[13.5px] tabular-nums text-[#6B7280]">{fmt(sc.users)}</span>
              <span className="text-right text-[13.5px] tabular-nums">{duration(sc.avg_duration_ms / 1000)}</span>
            </div>
          )) : <p className="border-t border-[#EEF0F4] py-8 text-center text-[13px] text-[#6B7280]">{data ? 'Aucun écran vu sur la période.' : ''}</p>}
        </section>

        <section className={`${card} px-7 pb-5 pt-5`}>
          <div className="flex items-center justify-between">
            <h2 className={cardTitle}>Versions de l’app</h2>
            <LinkButton onClick={() => navigate('/analytics?vue=versions')}>Gérer</LinkButton>
          </div>
          {versions.length === 0 ? <p className="py-6 text-center text-[13px] text-[#6B7280]">{data ? 'Aucune version mesurée.' : ''}</p> : versions.slice(0, 5).map(([v, n], i) => (
            <div key={v} className="mt-3.5 flex items-center gap-3">
              <span className="w-14 font-mono text-[13px] font-bold">{v}</span>
              <span className="h-2 flex-1 rounded-full bg-[#EEF0F5]"><span className="block h-2 rounded-full" style={{ width: `${pct(n, versionTotal)}%`, background: VERSION_COLORS[i] }} /></span>
              <b className="w-10 text-right text-[13px]">{pct(n, versionTotal)} %</b>
            </div>
          ))}
        </section>
      </div>
    </main>
  );
};

export default ManagementActivity;
